class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initIncludedBoxState();
    this.initIncludedBoxCarousel();
    this.initAddonScrollProgress();
  }

  /* Karusel „Obsah balení“ nemá posuvník (origin): myší se táhne, kolečko
     posouvá vodorovně. Delegované na drawer, protože obsah se překresluje. */
  initIncludedBoxCarousel() {
    const selector = '.cart-included-in-box[open] .cart-included-in-box__carousel';
    const isScrollable = (carousel) => carousel.scrollWidth > carousel.clientWidth + 1;
    const markScrollable = (carousel) => {
      carousel.closest('.cart-included-in-box').classList.toggle('cart-included-in-box--scroll', isScrollable(carousel));
    };
    let drag = null;

    this.addEventListener('pointerover', (event) => {
      const carousel = event.target.closest && event.target.closest(selector);
      if (carousel) markScrollable(carousel);
    });

    this.addEventListener('dragstart', (event) => {
      if (event.target.closest && event.target.closest(selector)) event.preventDefault();
    });

    this.addEventListener(
      'wheel',
      (event) => {
        const carousel = event.target.closest && event.target.closest(selector);
        if (!carousel || !isScrollable(carousel)) return;
        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);
        if (event.shiftKey && absY >= 1) {
          event.preventDefault();
          carousel.scrollLeft += event.deltaY;
        } else if (absX > absY && absX >= 1) {
          event.preventDefault();
          carousel.scrollLeft += event.deltaX;
        }
      },
      { passive: false }
    );

    this.addEventListener('pointerdown', (event) => {
      const carousel = event.target.closest && event.target.closest(selector);
      if (!carousel || event.button !== 0 || event.pointerType === 'touch' || !isScrollable(carousel)) return;
      event.preventDefault();
      drag = { carousel, pointerId: event.pointerId, startX: event.clientX, startLeft: carousel.scrollLeft };
      carousel.classList.add('is-dragging');
      try {
        carousel.setPointerCapture(event.pointerId);
      } catch (e) {}
    });

    this.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      event.preventDefault();
      drag.carousel.scrollLeft = drag.startLeft - (event.clientX - drag.startX);
    });

    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const { carousel } = drag;
      drag = null;
      carousel.classList.remove('is-dragging');
      try {
        carousel.releasePointerCapture(event.pointerId);
      } catch (e) {}
      const item = carousel.querySelector('.cart-included-in-box__item');
      const list = carousel.querySelector('.cart-included-in-box__list');
      if (!item || !list) return;
      const step = item.offsetWidth + (parseFloat(getComputedStyle(list).columnGap) || 8);
      carousel.scrollTo({ left: Math.round(carousel.scrollLeft / step) * step, behavior: 'smooth' });
    };
    this.addEventListener('pointerup', endDrag);
    this.addEventListener('pointercancel', endDrag);
  }

  /* „Obsah balení“ je ze serveru vždy otevřený. Co zákazník sbalí, zůstane
     sbalené i po překreslení draweru (změna množství, přidání). */
  initIncludedBoxState() {
    const storageKey = 'tbCartBoxClosed';
    const read = () => {
      try {
        return JSON.parse(sessionStorage.getItem(storageKey)) || [];
      } catch (e) {
        return [];
      }
    };
    const write = (keys) => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(keys));
      } catch (e) {}
    };
    const apply = () => {
      const closed = read();
      if (!closed.length) return;
      this.querySelectorAll('.cart-included-in-box[open]').forEach((box) => {
        if (closed.includes(box.dataset.lineKey)) box.removeAttribute('open');
      });
    };

    this.addEventListener(
      'toggle',
      (event) => {
        const box = event.target;
        if (!box.classList || !box.classList.contains('cart-included-in-box')) return;
        const key = box.dataset.lineKey;
        const closed = read().filter((k) => k !== key);
        if (!box.open) closed.push(key);
        write(closed);
      },
      true
    );

    new MutationObserver(apply).observe(this, { childList: true, subtree: true });
    apply();
  }

  /* Mobilní pás doplňků: tenký ukazatel posunu pod kartami (origin .scroll-progress). */
  initAddonScrollProgress() {
    this.addEventListener(
      'scroll',
      (event) => {
        const list = event.target;
        if (!list.classList || !list.classList.contains('add-on--card-list')) return;
        const bar = list.parentElement.querySelector('.scroll-progress');
        if (!bar || !list.scrollWidth) return;
        const visible = Math.min(1, (list.scrollLeft + list.clientWidth) / list.scrollWidth);
        bar.style.width = `${visible * 100}%`;
      },
      true
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /* Košík změněný odjinud (PDP, karta, chat): překreslit celý .drawer__inner,
     ne jen položky a patičku. Doplňky, souhrn a doprava jsou mimo
     cart-drawer-items a jinak by zůstaly staré. */
  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('.drawer__inner');
        const target = document.querySelector('#CartDrawer .drawer__inner');
        if (!source || !target) return;
        target.className = source.className;
        target.innerHTML = source.innerHTML;
        const drawer = document.querySelector('cart-drawer');
        const sourceDrawer = html.querySelector('cart-drawer');
        if (drawer && sourceDrawer) drawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
